import { ApiRoutes } from '../constants/routes';
import { fetcher } from '../utils/api';

class SearchService {
  /**
   * Perform global search across all content types
   * @param {string} query - Search query
   * @param {string} type - Content type filter (all, acts, tracks, albums, playlists, podcasts, creators, labels)
   * @param {number} page - Page number for pagination
   * @param {number} pageSize - Number of results per page
   * @param {string} sortBy - Sort order (newest, oldest, a-z, z-a, popular-up, popular-down)
   * @param {Array} tags - Array of tags to filter by
   * @param {string} tagMatchMode - Tag match mode ('any' or 'all')
   * @param {boolean} isExclusive - Filter by exclusive content only
   * @returns {Promise<Object>} Search results
   */
  async globalSearch(query, type = 'all', page = 1, pageSize = 20, sortBy = 'newest', tags = [], tagMatchMode = 'any', isExclusive = false) {
    try {
      const params = new URLSearchParams({
        q: query,
        type: type,
        page: page.toString(),
        page_size: pageSize.toString(),
        sort: sortBy
      });

      // Add tag filtering parameters if tags are provided
      if (tags && tags.length > 0) {
        tags.forEach(tag => {
          params.append('tags', tag);
        });
        params.append('tag_match', tagMatchMode);
      }

      // Add exclusive filter if provided
      if (isExclusive) {
        params.append('is_exclusive', 'true');
      }

      const url = `${ApiRoutes.GlobalSearch}?${params}`;
      console.log('SearchService: Making request to:', url);
      const result = await fetcher(url);
      console.log('SearchService: Got result:', result);
      return result;
    } catch (error) {
      console.error('SearchService: Global search error:', error);
      throw error;
    }
  }

  /**
   * Get search suggestions for autocomplete
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of suggestions
   * @returns {Promise<Array>} Array of suggestions
   */
  async getSuggestions(query, limit = 10) {
    try {
      if (query.length < 2) {
        return [];
      }

      const params = new URLSearchParams({
        q: query,
        limit: limit.toString()
      });

      const url = `${ApiRoutes.SearchSuggestions}?${params}`;
      const data = await fetcher(url);
      return data.suggestions || [];
    } catch (error) {
      console.error('Search suggestions error:', error);
      return [];
    }
  }

  /**
   * Get search statistics
   * @returns {Promise<Object>} Search statistics
   */
  async getSearchStats() {
    try {
      return await fetcher(ApiRoutes.SearchStats);
    } catch (error) {
      console.error('Search stats error:', error);
      throw error;
    }
  }

  /**
   * Search acts specifically
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} Act search results
   */
  async searchActs(query, page = 1, pageSize = 20) {
    return this.globalSearch(query, 'acts', page, pageSize);
  }

  /**
   * Search tracks specifically
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} Track search results
   */
  async searchTracks(query, page = 1, pageSize = 20) {
    return this.globalSearch(query, 'tracks', page, pageSize);
  }

  /**
   * Search albums specifically
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} Album search results
   */
  async searchAlbums(query, page = 1, pageSize = 20) {
    return this.globalSearch(query, 'albums', page, pageSize);
  }

  /**
   * Search playlists specifically
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} Playlist search results
   */
  async searchPlaylists(query, page = 1, pageSize = 20) {
    return this.globalSearch(query, 'playlists', page, pageSize);
  }

  /**
   * Search creators specifically
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} Creator search results
   */
  async searchCreators(query, page = 1, pageSize = 20) {
    return this.globalSearch(query, 'creators', page, pageSize);
  }

  /**
   * Search labels specifically
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} Label search results
   */
  async searchLabels(query, page = 1, pageSize = 20) {
    return this.globalSearch(query, 'labels', page, pageSize);
  }

  /**
   * Format search result for display
   * @param {Object} result - Search result object
   * @returns {Object} Formatted result
   */
  formatSearchResult(result) {
    const baseResult = {
      id: result.id,
      type: result.type,
      name: result.name,
      image: result.image,
      created: result.created,
      relevance_score: result.relevance_score
    };

    switch (result.type) {
      case 'act':
        return {
          ...baseResult,
          display_name: result.display_name,
          description: result.description,
          genres: result.genres,
          verified: result.verified
        };
      
      case 'track':
        return {
          ...baseResult,
          act_name: result.act_name,
          genres: result.genres,
          length: result.length,
          release_date: result.release_date
        };
      
      case 'album':
        return {
          ...baseResult,
          act_name: result.act_name,
          genres: result.genres,
          release_date: result.release_date
        };
      
      case 'playlist':
        return {
          ...baseResult,
          description: result.description,
          creator_name: result.creator_name,
          track_count: result.track_count
        };
      
      case 'creator':
        return {
          ...baseResult,
          email: result.email,
          bio: result.bio,
          user_type: result.user_type
        };
      
      case 'label':
        return {
          ...baseResult,
          description: result.description,
          genres: result.genres
        };
      
      default:
        return baseResult;
    }
  }

  /**
   * Group search results by type
   * @param {Array} results - Array of search results
   * @returns {Object} Results grouped by type
   */
  groupResultsByType(results) {
    const grouped = {
      acts: [],
      tracks: [],
      albums: [],
      playlists: [],
      creators: [],
      curators: [],
      labels: []
    };

    results.forEach(result => {
      // Map singular types to plural keys
      const typeMapping = {
        'act': 'acts',
        'track': 'tracks',
        'album': 'albums',
        'playlist': 'playlists',
        'creator': 'creators',
        'curator': 'curators',
        'label': 'labels'
      };
      
      const pluralType = typeMapping[result.type];
      if (pluralType && grouped[pluralType]) {
        grouped[pluralType].push(result);
      }
    });

    return grouped;
  }

  /**
   * Get content type display name
   * @param {string} type - Content type
   * @returns {string} Display name
   */
  getContentTypeDisplayName(type) {
    const typeNames = {
      'act': 'Artists',
      'track': 'Tracks',
      'album': 'Albums',
      'playlist': 'Playlists',
      'creator': 'Creators',
      'curator': 'Curators',
      'label': 'Labels'
    };
    return typeNames[type] || type;
  }

  /**
   * Get content type icon
   * @param {string} type - Content type
   * @returns {string} Icon name
   */
  getContentTypeIcon(type) {
    const typeIcons = {
      'act': 'person',
      'track': 'music_note',
      'album': 'album',
      'playlist': 'playlist_play',
      'creator': 'person',
      'curator': 'person',
      'label': 'business'
    };
    return typeIcons[type] || 'search';
  }
}

// Create and export a singleton instance
const searchService = new SearchService();
export default searchService;
